import { Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React from 'react';
import { ClientSideDataTable } from 'src/common/components/dataTable/clientSide';
import { formatDollars, formatNumberCommaSeparated } from '../../../../common';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { LoadingProgress } from '../../../../common/store/types';
import { theme } from '../../../../themes';
import { ModelAssociationItem } from '../store/types';

export interface Props {
  progress: LoadingProgress;
  data: ModelAssociationItem[];
}
export function AssociationsTable({ progress, data }: Props): JSX.Element {
  const defaultItems: ModelAssociationItem[] = [];

  const columns: DatatableColumn[] = [
    {
      label: 'AFSL NAME',
      name: 'afslName',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].afslName : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'Practice Name',  
      name: 'practiceName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'Adviser Name',  
      name: 'adviserName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'CLIENT ID',
      name: 'clientId',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].clientId : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'Client Name',  
      name: 'clientName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'PORTFOLIO ID',
      name: 'investmentServiceCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].investmentServiceCode : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'Portfolio Name',  
      name: 'portfolioName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      name: 'clientName',
      label: 'CLIENT AND PORTFOLIO NAME',
      textAlign: 'left',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <>
              <Typography variant={'h5'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex].clientName : ''}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex].investmentServiceName : ''}
              </Typography>
            </>
          );
        },
        download: false,     
        sort: true,
      },
      filterDataType: FilterDataType.string,
    },
    {
      label: 'ACCOUNT NUMBER',
      name: 'accountNumber',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].accountNumber : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'Model Code',
      name: 'modelCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].modelCode : ''}
            </Typography>
          );
        },
        download: false,     
        sort: false,
        viewColumns: false,
      },
    },
    {
      label: 'Account Name',  
      name: 'accountName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'Price',  
      name: 'price',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'Units',  
      name: 'units',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'UNITS & PRICE',
      name: 'units',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <>
              <Typography variant="h5">{formatNumberCommaSeparated(data[dataIndex]?.units ?? 0, 4)}</Typography>
              <Typography color={'textSecondary'} variant={'h6'}>
                {formatDollars(data[dataIndex]?.unitPrice, false, 4)}
              </Typography>
            </>
          );
        },
        download: false,
        sort: false,
        viewColumns: false,
      },
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      label: 'VALUE',
      name: 'value',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
           return <Typography variant="h5">{formatDollars(data[dataIndex]?.value)}</Typography>;
        },
        sort: false,
        viewColumns: false,
      },
    },
    
    {
      label: 'Price Date',  
      name: 'priceDate',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
  ];

  const options: MUIDataTableOptions = {
    search: true,
    download: true,
    filter: false,
  };

  const tableData = (data && data.length > 0 && !progress.isLoading ? data : defaultItems).map(item => ({
    ...item,
    priceDate: item.priceDate,
    practiceName: item.practiceName,
    adviserName: item.adviserName,
    clientName: item.clientName,
    portfolioName: item.investmentServiceName,
    accountName: item.accountName,
    unitPrice: item.unitPrice,
    units: item.units,
    afslName: item.afslName,
    value: item.value
  }));

  return (
    <ClientSideDataTable
      data={tableData}
      columns={columns}
      options={options}
      loadingProgress={progress}
      id="model-associations-datatable"
    />
  );
}
